/* eslint-disable react/no-array-index-key */
import { ReactNode, useState } from 'react';
import { RadioGroup } from '@headlessui/react';
import classNames from 'classnames';
import { BiCheck } from '@react-icons/all-files/bi/BiCheck';
import ContentLoader from 'react-content-loader';
import { Tooltip } from './Tooltip';

export interface Card {
  title: ReactNode;
  subtitle?: ReactNode;
  description?: ReactNode;
  key: string;
  disabled?: boolean;
  disabledMessage?: ReactNode;
}

export function SelectCard(props: {
  card: Card;
  className?: string;
  value: string;
}) {
  return (
    <RadioGroup.Option
      value={props.value}
      className={({ checked, active, disabled }) =>
        classNames(
          props.className,
          checked ? 'border-transparent' : 'border-gray-300',
          active ? 'border-blue-dark ring-2 ring-blue-dark' : '',
          disabled ? '!bg-gray-200 pointer-events-none' : '',
          'relative bg-white border rounded-md lg:rounded-lg shadow-sm px-3 py-4 sm:p-3 flex cursor-pointer focus:outline-none flex-1 z-20 transition-all focus:ring-2 focus:ring-blue-500 hover:border-gray-200 hover:shadow-md',
        )
      }
      defaultChecked
      disabled={props.card.disabled}
    >
      {({ checked, active }) => (
        <>
          <div className="flex-1 flex">
            <div className="flex flex-col">
              <RadioGroup.Label
                as="span"
                className="block text-sm font-medium text-gray-900"
              >
                {props.card.title}
              </RadioGroup.Label>
              {props.card.subtitle && (
                <RadioGroup.Description
                  as="span"
                  className="mt-1 flex items-center text-sm text-gray-500"
                >
                  {props.card.subtitle}
                </RadioGroup.Description>
              )}
              {props.card.description && (
                <RadioGroup.Description
                  as="span"
                  className="mt-6 text-sm font-medium text-gray-900"
                >
                  {props.card.description}
                </RadioGroup.Description>
              )}
            </div>
          </div>
          <BiCheck
            className={classNames(
              !checked && !active ? 'invisible' : '',
              'h-5 w-5 text-blue-dark',
            )}
            aria-hidden="true"
          />

          <div
            className={classNames(
              active ? 'border' : 'border-2',
              checked ? 'border-blue-dark' : 'border-transparent',
              'absolute -inset-px rounded-md lg:rounded-lg pointer-events-none',
            )}
            aria-hidden="true"
          />
        </>
      )}
    </RadioGroup.Option>
  );
}
export function LoadingCards(props: { numberOfCards?: number }) {
  const cards = Array.from(Array(props.numberOfCards));

  return (
    <>
      {cards.map((card, i) => (
        <div className="w-full h-full relative">
          <SelectCard
            card={{
              key: `${i}`,
              description: (
                <div className="w-full bg-gray-50 animation-pulse">
                  <span className="text-white">.</span>
                </div>
              ),
              subtitle: <span className="text-white">.</span>,
              title: <span className="text-white">.</span>,
            }}
            className="text-white pointer-events-none "
            value={`${i}`}
            key={i}
          />
          <div className="absolute bg-blue-100 top-0 z-50 w-full h-full rounded-md overflow-hidden border border-gray-200">
            <ContentLoader className="w-full">
              <rect x="0" y="0" rx={3} ry={3} width="100%" height="100%" />
            </ContentLoader>
          </div>
        </div>
      ))}
    </>
  );
}

export function TooltipSelectCard(props: {
  card: Card;
  className?: string;
  value: string;
}) {
  return (
    <Tooltip
      tooltip={props.card.disabledMessage ?? null}
      tooltipClassname="top-full md:bottom-full md:top-auto right-0 mb-0 shadow-md border border-gray-300"
      className="w-full md:w-[48.5%]"
    >
      <SelectCard card={props.card} value={props.value} />
    </Tooltip>
  );
}

export function SelectCards(props: {
  title?: string;
  cards?: Card[];
  onChange: (cardKey?: string) => void;
  loadingParams?: { isLoading: boolean; expectedAmountOfCards: number };
  selectedCard?: string;
  titleClassName?: string;
}) {
  const [selectedCard, setSelectedCard] = useState(props.selectedCard);
  return (
    <RadioGroup
      value={selectedCard}
      onChange={(card) => {
        props.onChange(card);
        setSelectedCard(card);
      }}
    >
      {!!props.title && (
        <RadioGroup.Label className={props.titleClassName}>
          {props.title}
        </RadioGroup.Label>
      )}

      <div className="flex flex-col space-y-4 md:flex-row md:justify-between md:space-y-0 md:space-x-4">
        {!props.loadingParams?.isLoading && props.cards ? (
          props.cards?.map((card) =>
            card.disabled ? (
              <TooltipSelectCard card={card} value={card.key} key={card.key} />
            ) : (
              <SelectCard key={card.key} card={card} value={card.key} />
            ),
          )
        ) : (
          <LoadingCards
            numberOfCards={props.loadingParams?.expectedAmountOfCards}
          />
        )}
      </div>
    </RadioGroup>
  );
}
